import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, ArrowRight, Loader2, BadgeCheck, Clock } from "lucide-react";
import { API, CONTACT, INPUT_CLS } from "../lib/site";
import { Reveal, Eyebrow, EmberField, GoldButton } from "../components/motion";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "private-commission", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          inquiry_type: form.type,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
      toast.success("Your message is on its way to the workshop.");
    } catch {
      toast.error(`The send was interrupted — please email ${CONTACT.email} directly.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative overflow-hidden pb-28 pt-40">
      <EmberField count={14} />
      <div className="animate-ember-pulse absolute right-[12%] top-[18%] h-96 w-96 rounded-full bg-amber-600/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-10">
        <Reveal><Eyebrow>Private Commission Concierge</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl">
            Begin the <span className="italic text-gold-gradient">conversation.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">
            Every Cozy Ember starts the same way — a conversation about your property, your
            evenings, and the piece that will anchor them.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="space-y-5">
            {[
              { icon: Mail, label: "Write to Us", value: CONTACT.email, href: CONTACT.emailHref, testId: "contact-email-card" },
              { icon: MapPin, label: "The Workshop", value: `${CONTACT.region} · serving Canada & the US`, testId: "contact-region-card" },
              { icon: Clock, label: "Concierge Hours", value: "Mon – Sat · 9am – 7pm EST", testId: "contact-hours-card" },
            ].map((c, i) => (
              <Reveal key={c.testId} delay={i * 0.08}>
                <div data-testid={c.testId} className="flex items-center gap-5 rounded-2xl border border-stone-800 bg-[#141110] p-6 transition-[border-color] duration-300 hover:border-amber-500/40">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-amber-500/40 bg-amber-950/30">
                    <c.icon size={18} className="text-amber-400" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-500/80">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} data-testid={`${c.testId}-link`} className="mt-1 block font-serif text-lg text-stone-100 transition-colors duration-200 hover:text-amber-400">{c.value}</a>
                    ) : (
                      <p className="mt-1 font-serif text-lg text-stone-100">{c.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <p className="border-l-2 border-amber-500/60 pl-5 font-serif text-base italic leading-relaxed text-stone-300">
                "Where did you get that?" — the question every Cozy Ember owner learns to love.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            {sent ? (
              <div data-testid="contact-success" className="flex h-full flex-col items-center justify-center rounded-2xl border border-amber-500/40 bg-amber-950/20 p-14 text-center">
                <BadgeCheck size={34} className="text-amber-400" />
                <p className="mt-5 font-serif text-3xl text-stone-100">Consider it begun.</p>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-stone-400">
                  Your message has been delivered to the workshop. Expect a personal reply at
                  <span className="text-amber-400"> {form.email} </span> within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-4 rounded-2xl border border-stone-800 bg-[#141110] p-7 sm:p-9">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="contact-form-name" required minLength={2} placeholder="Full name" className={INPUT_CLS} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="contact-form-email" required type="email" placeholder="Email address" className={INPUT_CLS} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="contact-form-phone" type="tel" placeholder="Your phone (optional)" className={INPUT_CLS} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <select
                    data-testid="contact-form-type"
                    className={`${INPUT_CLS} appearance-none`}
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="private-commission">Private Commission</option>
                    <option value="hospitality-trade">Hospitality / Trade</option>
                    <option value="stone-materials">Stone & Materials Question</option>
                    <option value="general">Something Else</option>
                  </select>
                </div>
                <textarea data-testid="contact-form-message" required minLength={5} rows={5} placeholder="Tell us about your property, your timeline, and what you're imagining…" className={`${INPUT_CLS} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <GoldButton data-testid="contact-form-submit" type="submit" disabled={sending} className="w-full sm:w-auto">
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <>Send to the Workshop <ArrowRight size={15} /></>}
                </GoldButton>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </div>
  );
}
